import { type ClassValue, clsx } from "clsx";
import { format } from "dnum";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function multiplyToWad(a: number, b: number): bigint {
  return BigInt(a) * BigInt(b * 1e18);
}

export const shortenAddress = (address: string) => {
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

export const formatToken = (amount: bigint, decimals = 18, compact = false, digits?: number) => {
  // If digits not specified, use intelligent defaults
  if (digits === undefined) {
    digits = 2;
  }

  return format([amount, decimals], {
    digits,
    trailingZeros: true,
    compact: compact,
  });
};

/**
 * Format token amounts with intelligent decimal display
 * Shows more decimals for smaller numbers, fewer for larger
 */
export const formatTokenIntelligent = (amount: bigint, decimals = 18, compact = false) => {
  // Convert to number to check magnitude
  const numValue = Number(format([amount, decimals], { digits: 18 }));

  // Determine optimal decimal places based on value
  let optimalDigits: number;

  if (numValue === 0) {
    optimalDigits = 0;
  } else if (numValue < 0.00001) {
    // Ultra small: show up to 8 decimals
    optimalDigits = 8;
  } else if (numValue < 0.001) {
    // Very small: show 6 decimals
    optimalDigits = 6;
  } else if (numValue < 0.1) {
    // Small: show 5 decimals
    optimalDigits = 5;
  } else if (numValue < 1) {
    // Less than 1: show 4 decimals
    optimalDigits = 4;
  } else if (numValue < 100) {
    // 1-100: show 3 decimals
    optimalDigits = 3;
  } else if (numValue < 10000) {
    // 100-10k: show 2 decimals
    optimalDigits = 2;
  } else if (numValue < 1000000) {
    // 10k-1M: show 1 decimal
    optimalDigits = 1;
  } else {
    // Over 1M: no decimals if compact, 2 if not
    optimalDigits = compact ? 0 : 2;
  }

  return format([amount, decimals], {
    digits: optimalDigits,
    trailingZeros: false,
    compact: compact,
  });
};

export const formatTokenRounded = (amount: bigint, decimals = 18) => {
  return format([amount, decimals], {
    digits: 0,
    trailingZeros: false,
  });
};

export const calculateTimeRemaining = (startTime: number, currentTime: number) => {
  const timeRemaining = startTime - currentTime;
  if (timeRemaining <= 0) {
    return "0m";
  }
  const days = Math.floor(timeRemaining / (3600 * 24));
  const hours = Math.floor((timeRemaining % (3600 * 24)) / 3600);
  const minutes = Math.floor((timeRemaining % 3600) / 60);
  let timeString = "";
  if (days > 0) timeString += `${days}d `;
  if (hours > 0 || days > 0) timeString += `${hours}h `;
  if (minutes > 0 || hours > 0 || days > 0) timeString += `${minutes}m`;
  return timeString;
};

export function formatAddresses(wallets: { chain: string; address: string }[]) {
  return wallets
    .map((w) => {
      if (w.chain === "SOL" || w.chain === "ECLIPSE" || w.chain === "BTC") return w.address;
      return w.address.toLowerCase();
    })
    .join(",");
}

/**
 * Converts a strike price to a fully diluted valuation (FDV) market cap
 * Based on a total token supply of 100,000,000,000 (1e11)
 * A strike of 1 = $1k FDV, 50 = $50k FDV, 1000 = $1M FDV
 */
export const strikeToMarketCap = (strike: number) => strike * 1000;

export function formatCompactNumber(number: number): string {
  if (number >= 1_000_000_000) {
    return `${(number / 1_000_000_000).toFixed(0)}b`;
  }
  if (number >= 1_000_000) {
    return `${(number / 1_000_000).toFixed(0)}m`;
  }
  if (number >= 1_000) {
    return `${(number / 1_000).toFixed(0)}k`;
  }
  return number.toString();
}

export const padWithZero = (num: number, targetLength: number): string => {
  return num.toString().padStart(targetLength, "0");
};
